import React from 'react'
import { Pressable, ScrollView, View } from 'react-native'

import { Network } from '@ambire-common/interfaces/network'
import shortenAddress from '@ambire-common/utils/shortenAddress'
import { AccountOpStatus } from '@ambire-common/libs/accountOp/types'
import CopyIcon from '@common/assets/svg/CopyIcon'
import LeftArrowIcon from '@common/assets/svg/LeftArrowIcon'
import BottomSheet from '@common/components/BottomSheet'
import BaseAddress from '@common/components/HumanizerAddress/components/BaseAddress'
import Text from '@common/components/Text'
import { useTranslation } from '@common/config/localization'
import useTheme from '@common/hooks/useTheme'
import useToast from '@common/hooks/useToast'
import spacings, { SPACING_SM } from '@common/styles/spacings'
import { setStringAsync } from '@common/utils/clipboard'
import Footer from '@web/modules/settings/components/TransactionHistory/SubmittedTransactionSummary/Footer'
import { getTruncatedTxnHash } from '@web/modules/settings/components/TransactionHistory/SubmittedTransactionSummary/helpers'
import getStyles from '@web/modules/settings/components/TransactionHistory/SubmittedTransactionSummary/styles'
import TransactionSummary, {
  sizeMultiplier
} from '@web/modules/sign-account-op/components/TransactionSummary'

import type { Modalize } from 'react-native-modalize'

// Private counterpart of the public SummaryDetailsSheet. The public detail view
// (SummaryDetails) is built around an AccountOp shape (humanized calls, balance
// changes, identifiedBy, delegation meta), none of which exist for a RAILGUN
// TransactionHistoryItem — so we render the same sheet chrome here but feed it
// the already-computed private fields from PrivateSubmittedTransactionSummary.
type Props = {
  sheetRef: React.RefObject<Modalize>
  closeBottomSheet: () => void
  modalType: 'modal' | 'bottom-sheet'
  network: Network
  size: 'sm' | 'md' | 'lg'
  call: any
  chainId: bigint
  txid: string
  isPrivateCounterparty: boolean
  isEvmAddress: boolean
  counterpartyAddress: string
  extraMessage?: string | null
  status: AccountOpStatus
  gasFeePayment: any
  isFeeLoading: boolean
  timestamp: number
  isBlocked?: boolean
  ppoiUrl?: string
  onUnshieldToOrigin?: () => void
}

const PrivateSummaryDetailsSheet = ({
  sheetRef,
  closeBottomSheet,
  modalType,
  network,
  size,
  call,
  chainId,
  txid,
  isPrivateCounterparty,
  isEvmAddress,
  counterpartyAddress,
  extraMessage,
  status,
  gasFeePayment,
  isFeeLoading,
  timestamp,
  isBlocked,
  ppoiUrl,
  onUnshieldToOrigin
}: Props) => {
  const { styles, theme } = useTheme(getStyles)
  const { t } = useTranslation()
  const { addToast } = useToast()

  return (
    <BottomSheet
      sheetRef={sheetRef}
      closeBottomSheet={closeBottomSheet}
      type={modalType}
      adjustToContentHeight={modalType === 'modal'}
      containerInnerWrapperStyles={styles.sheetContainer}
      style={{
        maxWidth: 720,
        paddingVertical: 0,
        paddingHorizontal: 0,
        overflow: 'hidden'
      }}
    >
      <View style={styles.sheetContainer}>
        <View style={styles.sheetHeader}>
          <Pressable
            onPress={() => {
              closeBottomSheet()
            }}
            style={styles.sheetHeaderBackButton}
          >
            <LeftArrowIcon color={theme.secondaryText} />
          </Pressable>
          <Text weight="semiBold" style={styles.sheetHeaderTitle}>
            {t('Activity information')}
          </Text>
          <View style={styles.sheetHeaderBackButton} />
        </View>
        <ScrollView
          bounces={false}
          showsVerticalScrollIndicator={false}
          style={styles.sheetScroll}
          contentContainerStyle={styles.sheetScrollContent}
        >
          <View style={spacings.ph}>
            <View style={styles.modalSection} testID="activity-transaction-details-step">
              <Text appearance="tertiaryText" weight="medium" style={spacings.mbSm}>
                {t('Transaction details')}
              </Text>
              <TransactionSummary
                style={styles.summaryItem}
                call={call}
                chainId={chainId}
                isHistory
                enableExpand
                size={size}
                hideLinks
              />
              {!isPrivateCounterparty && !isEvmAddress && (
                <View
                  style={{
                    paddingHorizontal: 18.2,
                    paddingBottom: 6.8,
                    marginTop: -6.8
                  }}
                >
                  <BaseAddress address={counterpartyAddress} chainId={chainId} hideLinks>
                    {shortenAddress(counterpartyAddress, 16)}
                  </BaseAddress>
                </View>
              )}
              {!!extraMessage && (
                <View style={[spacings.phMd, spacings.pbMi]}>
                  <Text
                    fontSize={12 * sizeMultiplier[size]}
                    appearance="secondaryText"
                    weight="medium"
                    numberOfLines={6}
                  >
                    {extraMessage}
                  </Text>
                </View>
              )}
            </View>
            {!!txid && (
              <View
                style={[styles.modalConfirmedRow, spacings.mbSm]}
                testID="activity-transaction-hash-step"
              >
                <View style={styles.modalStepRow}>
                  <Text appearance="tertiaryText" weight="medium">
                    {t('Transaction hash')}
                  </Text>
                  <View style={styles.modalStepRowRight}>
                    <Text fontSize={14} appearance="secondaryText">
                      {getTruncatedTxnHash(txid)}
                    </Text>
                    <Pressable
                      onPress={() => {
                        void setStringAsync(txid)
                        addToast(t('Copied to clipboard!') as string, { timeout: 2500 })
                      }}
                      style={styles.modalHashCopyButton}
                    >
                      <CopyIcon width={16} height={16} color={theme.primaryText} />
                    </Pressable>
                  </View>
                </View>
              </View>
            )}
          </View>
        </ScrollView>
        <Footer
          size={size}
          network={network}
          txnId={txid}
          identifiedBy={{} as any}
          accountAddr=""
          gasFeePayment={gasFeePayment}
          isFeeLoading={isFeeLoading}
          status={status}
          timestamp={timestamp}
          isBlocked={isBlocked}
          ppoiUrl={ppoiUrl}
          onUnshieldToOrigin={onUnshieldToOrigin}
        />
      </View>
    </BottomSheet>
  )
}

export default React.memo(PrivateSummaryDetailsSheet)
